import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useRealtimeAdapter } from '@/contexts/RealtimeContext';
import { conversationsRepository } from '@/features/messages/repositories/supabaseConversationsRepository';

export interface Conversation {
  id: string;
  other_user_id: string;
  other_user_name: string;
  other_user_username: string | null;
  other_user_picture: string | null;
  other_user_belt_rank: string;
  other_user_academy: string | null;
  other_user_is_premium: boolean;
  last_message_at: string | null;
  last_message_preview: string | null;
  last_message_is_mine: boolean | null;
  unread_count: number;
  created_at: string;
}

export const useConversations = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const realtimeAdapter = useRealtimeAdapter();

  const { data: unreadCount = 0, refetch: refetchUnreadCount } = useQuery({
    queryKey: ['messages', 'unread-count', user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;
      try {
        return await conversationsRepository.getUnreadCount();
      } catch (error) {
        console.error('Error fetching unread messages count:', error);
        return 0;
      }
    },
    enabled: !!user?.id,
    refetchInterval: 30_000,
  });

  const {
    data: conversations = [],
    isLoading,
    error: conversationsError,
    refetch: refetchConversations,
  } = useQuery({
    queryKey: ['conversations', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      try {
        return await conversationsRepository.getConversations();
      } catch (error) {
        console.error('[useConversations] get_conversations failed:', error);
        throw error;
      }
    },
    enabled: !!user?.id,
    retry: 1,
  });

  const { mutateAsync: createOrGetConversation, isPending: isCreating } =
    useMutation({
      mutationFn: (otherUserId: string) =>
        conversationsRepository.createOrGetConversation(otherUserId),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['conversations', user?.id] });
      },
    });

  useEffect(() => {
    if (!user?.id) return;

    return realtimeAdapter.subscribeToUserMessages(user.id, () => {
      refetchUnreadCount();
      refetchConversations();
    });
  }, [user?.id, refetchUnreadCount, refetchConversations, realtimeAdapter]);

  if (!user) {
    return {
      unreadCount:             0,
      conversations:           [] as Conversation[],
      isLoading:               false,
      isCreating:              false,
      createOrGetConversation: async (_: string): Promise<string> => { throw new Error('Not authenticated'); },
      refetchConversations:    () => {},
    };
  }

  return {
    unreadCount,
    conversations,
    isLoading,
    isError: !!conversationsError,
    conversationsError,
    isCreating,
    createOrGetConversation,
    refetchConversations,
  };
};
